import { useContext, useState, useEffect } from 'react'
import LocationDropDown from '../../global/components/location-drop-down/LocationDropDown'
import PrimaryButton from '../../global/components/buttons/primary-button/PrimaryButton'
import TextField from '@mui/material/TextField'
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material'
import styles from './styles.module.css'
import { fetchData } from '../../global/utils/fetch'
import {
  getActiveTechniciansForLocation,
  testScheduleChangeImpact,
} from '../api'
import { ToastNotificationContext } from '../../global/context/toast-context/ToastNotificationContext'
import { DayAnalysisResult } from '../../../../app/types/technician-impact-analysis'

interface ActiveTechnician {
  objectId: string
  name: string
  email: string
  workDayFrom: string
  workDayTo: string
  lunchDuration: number
}

export default function ScheduleChangeImpactTester() {
  const [form, setForm] = useState({
    locationObjectId: '',
    date: '',
    technicianId: '',
    workDay: true,
    startTime: '',
    endTime: '',
    lunchDuration: 30,
  })

  const [technicians, setTechnicians] = useState<ActiveTechnician[]>([])
  const [dayAnalysisResult, setDayAnalysisResult] =
    useState<DayAnalysisResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [loadingTechnicians, setLoadingTechnicians] = useState(false)
  const { toastStatus, setToastStatus } = useContext(ToastNotificationContext)

  // Load technicians when location changes
  useEffect(() => {
    if (form.locationObjectId) {
      loadTechnicians()
    } else {
      setTechnicians([])
      setForm((prev) => ({ ...prev, technicianId: '' }))
    }
  }, [form.locationObjectId])

  const loadTechnicians = async () => {
    try {
      setLoadingTechnicians(true)
      const response = await fetchData<{
        locationId: string
        locationName: string
        activeTechnicians: ActiveTechnician[]
      }>(getActiveTechniciansForLocation(form.locationObjectId))

      setTechnicians(response.activeTechnicians)

      // Auto-populate form with first technician's current schedule
      if (response.activeTechnicians.length > 0) {
        const firstTech = response.activeTechnicians[0]
        setForm((prev) => ({
          ...prev,
          technicianId: firstTech.objectId,
          startTime: firstTech.workDayFrom,
          endTime: firstTech.workDayTo,
          lunchDuration: firstTech.lunchDuration,
        }))
      }
    } catch (error) {
      console.error('Error loading technicians:', error)
      setToastStatus({
        ...toastStatus,
        message: `Error loading technicians: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        severity: 'error',
        isOpen: true,
      })
    } finally {
      setLoadingTechnicians(false)
    }
  }

  const handleTechnicianChange = (technicianId: string) => {
    const selectedTech = technicians.find((t) => t.objectId === technicianId)
    if (selectedTech) {
      setForm((prev) => ({
        ...prev,
        technicianId,
        startTime: selectedTech.workDayFrom,
        endTime: selectedTech.workDayTo,
        lunchDuration: selectedTech.lunchDuration,
      }))
    }
  }

  const handleTest = async () => {
    const {
      locationObjectId,
      date,
      technicianId,
      workDay,
      startTime,
      endTime,
      lunchDuration,
    } = form

    if (!locationObjectId || !date || !technicianId) {
      return alert('Please select location, date, and technician')
    }

    try {
      setIsLoading(true)
      setDayAnalysisResult(null)

      const result = await fetchData<DayAnalysisResult>(
        testScheduleChangeImpact(technicianId, {
          day: new Date(date),
          workDay,
          startTime: workDay ? startTime : undefined,
          endTime: workDay ? endTime : undefined,
          lunchDuration: workDay ? lunchDuration : undefined,
        }),
      )

      setDayAnalysisResult(result)

      setToastStatus({
        ...toastStatus,
        message: `Test completed: ${result.droppedOrderCount} orders would be dropped.`,
        severity: Number(result.droppedOrderCount) > 0 ? 'warning' : 'success',
        isOpen: true,
      })
    } catch (error) {
      console.error('Error testing schedule change impact:', error)
      setToastStatus({
        ...toastStatus,
        message: `Error testing impact: ${
          error instanceof Error ? error.message : 'Unknown error'
        }`,
        severity: 'error',
        isOpen: true,
      })
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className={styles.toolContainer}>
      <h2>Schedule Change Impact Tester</h2>
      <p className={styles.toolDescription}>Test how schedule changes affect order assignments for a single day</p>

      <div className={styles.inputsContainer}>
        <LocationDropDown
          onLocationChange={(locationObjectId: string) =>
            setForm({
              ...form,
              locationObjectId,
              technicianId: '',
              startTime: '',
              endTime: '',
              lunchDuration: 30,
            })
          }
        />

        <TextField
          type='date'
          label='Test Date'
          name='date'
          value={form.date}
          onChange={(e) => setForm({ ...form, date: e.target.value })}
          InputLabelProps={{ shrink: true }}
          fullWidth
        />

        <FormControl
          fullWidth
          disabled={loadingTechnicians || !form.locationObjectId}
        >
          <InputLabel>Technician</InputLabel>
          <Select
            value={form.technicianId}
            label='Technician'
            onChange={(e) => handleTechnicianChange(e.target.value)}
          >
            {technicians.map((tech) => (
              <MenuItem
                key={tech.objectId}
                value={tech.objectId}
              >
                {tech.name} ({tech.workDayFrom}-{tech.workDayTo})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControlLabel
          control={
            <Switch
              checked={form.workDay}
              onChange={(e) => setForm({ ...form, workDay: e.target.checked })}
            />
          }
          label='Work Day'
        />

        {form.workDay && (
          <>
            <TextField
              type='time'
              label='Start Time'
              name='startTime'
              value={form.startTime}
              onChange={(e) => setForm({ ...form, startTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              type='time'
              label='End Time'
              name='endTime'
              value={form.endTime}
              onChange={(e) => setForm({ ...form, endTime: e.target.value })}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />

            <TextField
              type='number'
              label='Lunch Duration (minutes)'
              name='lunchDuration'
              value={form.lunchDuration}
              onChange={(e) =>
                setForm({
                  ...form,
                  lunchDuration: parseInt(e.target.value) || 0,
                })
              }
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </>
        )}

        <PrimaryButton
          buttonName='Test Impact'
          onClick={handleTest}
          disabled={
            isLoading ||
            !form.locationObjectId ||
            !form.date ||
            !form.technicianId
          }
        >
          <>
            {isLoading && (
              <div style={{ marginRight: '12px' }}>
                <CircularProgress size={20} />
              </div>
            )}
            Test Schedule Impact
          </>
        </PrimaryButton>
      </div>

      {dayAnalysisResult && dayAnalysisResult.droppedOrders && (
        <div
          style={{
            marginTop: '20px',
            padding: '16px',
            border: '1px solid #ccc',
            borderRadius: '4px',
          }}
        >
          <div
            style={{
              marginTop: '12px',
              padding: '12px',
              backgroundColor:
                dayAnalysisResult.droppedOrders.length > 0
                  ? '#fff3cd'
                  : '#d4edda',
              borderRadius: '4px',
            }}
          >
            {dayAnalysisResult.droppedOrders.length > 0 ? (
              <div style={{ marginTop: '12px' }}>
                <h4>Dropped Orders:</h4>
                <ul>
                  {dayAnalysisResult.droppedOrders.map((order, index) => (
                    <li key={index}>
                      Order {order.objectId}{' '}
                      {order.workOrderId ? `(WO: ${order.workOrderId})` : ''}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <div>No orders would be dropped with this schedule change.</div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
